import React, { useState } from "react";
import { useForm } from "react-hook-form";

import Title from "../LoanTermDeviation/Title";
import { useUtilityStates } from "./useStates";
import Label from "../../components/atomic/Label";
import Button from "../../components/atomic/Button";
import { SliderContainerType } from "./SliderContainer";
import { notify } from "../../shared/context/AlertContext";
import GetAuthContext from "../../shared/context/AuthContext";
import Input from "../../components/composed/FormElements/Input";
import { LoanApplicationService } from "../../services/LoanApplication";
import ErrorTextBox from "../../components/atomic/ErrorBox/ErrorTextBox";
import DropdownWithSearch from "../../components/atomic/DropdownWithSearch";
import { sendLinkHandlerFn } from "./ExisitingOffer/CardUtils";

import { getCurrentOrganisation } from "@unified-dashboard/src/shared/utils/Global";
import { ORG_NAMES } from "@unified-dashboard/src/shared/utils/constants";

type data = {
  phone: string;
  pan: string;
};

export default function PreApproved(props: SliderContainerType) {
  const { close } = props;
  const { fetchToken } = GetAuthContext();

  const [loading, setLoading] = React.useState(false);
  const [programeeOption, setProgrameeOption] = useState();
  const [showProgrameeType, setShowProgrameeType] = useState(false);
  const [showProgrameeTypeError, setShowProgrameeTypeError] = useState(false);
  const [showPreapprovedError, setShowPreapprovedError] = useState(false);
  const { setExistingOffers, setSidebar } = useUtilityStates((state) => state);
  const organization = getCurrentOrganisation();
  const isMFLBL = organization === ORG_NAMES.MFL;

  const {
    register,
    setValue,
    handleSubmit,
    watch,
    formState: { errors }
  } = useForm();

  const panValue = watch("pan", "");
  const mobileRegister = register("mobile", {
    required: "Phone Number is required.",
    validate: {
      length: (value) => {
        if (!value) return "Phone Number is required.";
        if (value.length !== 10)
          return "Phone Number must be exactly 10 digits long.";
        return true;
      },
      format: (value) => {
        if (!value) return "Phone Number is required.";
        if (!/^[0-9]+$/.test(value))
          return "Phone Number can only contain digits.";
        return true;
      },
      startsWithValidDigit: (value) => {
        if (!value) return "Phone Number is required.";
        if (!/^[6-9]/.test(value))
          return "Phone Number must start with 6, 7, 8, or 9.";
        return true;
      }
    }
  });

  const programeeOptions = [
    { name: "EDI", value: "EDI" },
    { name: "EMI", value: "EMI" }
  ];

  const onSubmitHandler = async (data: data) => {
    if (showProgrameeType && !programeeOption) {
      setShowProgrameeTypeError(true);
      return false;
    } else {
      setShowProgrameeTypeError(false);
    }
    setLoading(true);

    setShowPreapprovedError(false);
    const payload = {
      pageName: props?.pageName,
      token: fetchToken(),
      payload: data
    };

    if (showProgrameeType) {
      await sendLinkHandlerFn({
        token: fetchToken(),
        pageName: props?.pageName,
        offer: {
          mobile: data?.mobile,
          sourceEntityID: "8efd25fb-9c1f-4adc-90a8-7e2cc0b17775",
          partner_code: "mfl_erdge",
          programee: programeeOption?.value
        }
      });
      close();
      return;
    }

    const response = (await LoanApplicationService?.getPreApprovedOffers(
      payload
    )) as any;

    let condition;

    if (isMFLBL) {
      if (response?.data?.offers?.length > 0) {
        condition = "default";
      } else if (!response?.data?.offersFound && response?.data?.message) {
        notify({
          title: "Error",
          type: "error",
          text: response?.data?.message
        });

        return;
      } else setShowProgrameeType(true);
    } else {
      condition =
        !response?.status || !response?.data
          ? "error"
          : response?.data?.offersFound && !data?.pan
            ? "preApprovedOffer"
            : (!response?.data?.offersFound && !data?.pan) ||
                response?.data?.offersFound
              ? "showProgram"
              : programeeOption
                ? "sendLink"
                : "default";
    }

    switch (condition) {
      case "error":
        notify({
          title: "Error",
          type: "error",
          text: response?.error || "No Pre-Approved offers found."
        });
        break;

      case "showProgram":
        setShowProgrameeType(true);
        break;

      case "preApprovedOffer":
        setShowPreapprovedError(true);
        if (!response?.data?.offersFound) {
          setShowProgrameeType(true);
        }
        break;

      case "default":
        notify({
          type: "success",
          title: "Success",
          text: "Get Pre Approved Offers for you."
        });

        setTimeout(() => {
          setExistingOffers(response?.data?.offers);
          setSidebar({
            isOpen: true,
            current: "existing-offers"
          });
        }, 300);
        break;
    }
    setLoading(false);
  };

  return (
    <div className="p-10">
      <Title
        close={close}
        title="Pre-Approved Loan Offer"
      />
      <div className="text-xs text-[#4C4C4D] font-normal ">
        Enter User details to get access to all of their existing offers.
      </div>
      <form
        className="mt-10"
        onSubmit={handleSubmit(onSubmitHandler)}
      >
        <fieldset>
          <Input
            requiredIcon
            type="text"
            label="Phone Number"
            placeholder="Enter Phone Number"
            maxLength={10}
            inputMode="numeric"
            pattern="[6-9][0-9]*"
            register={{
              ...mobileRegister,
              onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
                const digitsOnly = event.target.value
                  .replace(/\D/g, "")
                  .slice(0, 10);
                event.target.value = digitsOnly;
                mobileRegister.onChange(event);
              }
            }}
            error={errors.mobile}
            errorText={errors?.mobile?.message as string}
          />
        </fieldset>

        {showPreapprovedError && (
          <ErrorTextBox text="This customer might have Pre-approved offer, please enter PAN number to check." />
        )}

        <fieldset className="mt-5">
          <Label
            name="pan"
            label="Pan Number"
            requiredIcon={isMFLBL}
          />
          <input
            type="text"
            {...register("pan", {
              required: isMFLBL ? "PAN Number is required." : false,
              validate: (value) =>
                value === "" ||
                /^[A-Z]{5}[0-9]{4}[A-Z]$/.test(value) ||
                "Invalid PAN Number format."
            })}
            value={panValue}
            placeholder="Enter PAN Number"
            onChange={(e) => {
              const uppercaseValue = e.target.value.toUpperCase();
              setValue("pan", uppercaseValue, { shouldValidate: true });
            }}
            className="block w-full rounded-md border-gray-200 sm:text-sm lg:text-xs px-4 py-2 placeholder:text-[#CECFCF]"
          />
          {errors?.pan?.message && (
            <ErrorTextBox text={errors?.pan?.message as string} />
          )}
        </fieldset>

        {showProgrameeType && (
          <fieldset className="mt-5">
            <Label
              name="programeeType"
              label="Program type"
              requiredIcon={true}
            />

            <DropdownWithSearch
              multiple={false}
              hasSearch={false}
              hasSelectAll={false}
              options={programeeOptions}
              placeholder="Select Program type"
              onChange={(data: any) => {
                setProgrameeOption(data);
              }}
              requiredIcon={true}
              styles={{ container: "w-full" }}
              selectedItems={programeeOption}
            />
            {showProgrameeTypeError && (
              <ErrorTextBox text={"Please Select Programee Type" as string} />
            )}
          </fieldset>
        )}

        <fieldset className="w-full mt-6">
          <Button
            type="submit"
            color="primary"
            disabled={loading}
            className="w-full"
          >
            Check offers
          </Button>
        </fieldset>
      </form>
    </div>
  );
}
